import React, { useState, useEffect } from 'react';
import { Phone, Send, CheckCircle, AlertCircle, Clock, Shield, Users, FileText } from 'lucide-react';

import SMSQuestionnaireService from '../../../services/smsQuestionnaireService';
import { getDynamicPhoneNumber } from '../../../services/constants';

const EmergencyBailAssistanceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    detaineeName: '',
    location: '',
    relationshipToDetainee: '',
    urgencyLevel: 'high',
    financialCapability: '',
    smsConsent: false,
    termsAccepted: false,
    privacyPolicyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [smsResults, setSmsResults] = useState(null);
  const [fallbackPhone, setFallbackPhone] = useState('(916) 299-9182');
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [showLegalModal, setShowLegalModal] = useState(null);

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'));
      } catch (error) {
        setFallbackPhone('(916) 256-3468');
      }
    };

    updateFallbackPhone();
  }, []);

  // Load legal documents on component mount
  useEffect(() => {
    const loadLegalDocuments = async () => {
      try {
        const documents = await SMSQuestionnaireService?.getLegalDocuments();
        setLegalDocuments(documents || []);
      } catch (error) {
        console.error('Failed to load legal documents:', error);
      }
    };

    loadLegalDocuments();
  }, []);

  // 🚀 CRITICAL FIX: Add component mount logging to verify component renders
  useEffect(() => {
    console.log('🎯 EmergencyBailAssistanceForm MOUNTED successfully');
    console.log('📋 Initial form state:', formData);
  }, []);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value?.replace(/\D/g, '');
    
    // Format to (XXX) XXX-XXXX pattern
    if (phoneNumber?.length === 0) return '';
    if (phoneNumber?.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber?.length <= 6) return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3)}`;
    return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3, 6)}-${phoneNumber?.slice(6, 10)}`;
  };

  // 🆕 NEW FUNCTION: Convert formatted phone to E.164 format for Supabase
  const convertToE164Format = (formattedPhone) => {
    // Remove all non-digit characters from formatted phone
    const digitsOnly = formattedPhone?.replace(/\D/g, '');
    
    // Add +1 country code for US numbers
    return `+1${digitsOnly}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target || {};
    
    // Apply phone formatting for mobile field
    const formattedValue = name === 'mobile' ? formatPhoneNumber(value) : value;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : formattedValue
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    // 🆕 CRITICAL DEBUG: Log validation start
    console.log('🔍 VALIDATION START - Form data:', {
      name: formData?.name,
      mobile: formData?.mobile,
      detaineeName: formData?.detaineeName,
      relationshipToDetainee: formData?.relationshipToDetainee,
      financialCapability: formData?.financialCapability,
      urgencyLevel: formData?.urgencyLevel,
      smsConsent: formData?.smsConsent,
      termsAccepted: formData?.termsAccepted,
      privacyPolicyAccepted: formData?.privacyPolicyAccepted
    });

    const {
      name,
      mobile,
      detaineeName,
      relationshipToDetainee,
      urgencyLevel,
      financialCapability,
      smsConsent,
      termsAccepted,
      privacyPolicyAccepted
    } = formData;

    // Name validation - check for actual content
    if (!name || name?.trim() === '') {
      console.error('❌ VALIDATION FAILED: Name is empty');
      setError('❌ Please enter your name');
      return false;
    }

    // Phone number validation - check for actual content first
    if (!mobile || mobile?.trim() === '') {
      console.error('❌ VALIDATION FAILED: Phone number is empty');
      setError('❌ Please enter your phone number');
      return false;
    }

    // Phone number format validation - must match (XXX) XXX-XXXX
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!phoneRegex?.test(mobile)) {
      console.error('❌ VALIDATION FAILED: Phone number format invalid', mobile);
      setError('❌ Please enter a valid phone number in format: (XXX) XXX-XXXX');
      return false;
    }

    // Detainee name validation
    if (!detaineeName || detaineeName?.trim() === '') {
      console.error('❌ VALIDATION FAILED: Detainee name is empty');
      setError('❌ Please enter the name of the person who needs bail');
      return false;
    }

    // Relationship validation - must have actual selection
    if (!relationshipToDetainee || relationshipToDetainee?.trim() === '' || relationshipToDetainee === 'Select relationship') {
      console.error('❌ VALIDATION FAILED: Relationship not selected');
      setError('❌ Please select your relationship to the person who needs bail');
      return false;
    }

    // Budget validation
    if (!financialCapability || financialCapability?.trim() === '' || financialCapability === 'Select budget range') {
      console.error('❌ VALIDATION FAILED: Budget not selected');
      setError('❌ Please select your budget range for bail bond services');
      return false;
    }

    // Urgency level validation - should always have value from default, but check anyway
    if (!urgencyLevel || urgencyLevel?.trim() === '' || urgencyLevel === 'Select urgency level') {
      console.error('❌ VALIDATION FAILED: Urgency level not selected');
      setError('❌ Please select the urgency level');
      return false;
    }

    // SMS consent validation - must be explicitly checked
    if (smsConsent !== true) {
      console.error('❌ VALIDATION FAILED: SMS consent not checked', smsConsent);
      setError('❌ SMS consent is required to receive emergency assistance updates. Please check the SMS consent box to continue.');
      return false;
    }

    // Terms of Service validation - must be explicitly checked
    if (termsAccepted !== true) {
      console.error('❌ VALIDATION FAILED: Terms not accepted', termsAccepted);
      setError('❌ You must accept the Terms of Service to proceed. Please check the Terms of Service checkbox.');
      return false;
    }

    // Privacy Policy validation - must be explicitly checked
    if (privacyPolicyAccepted !== true) {
      console.error('❌ VALIDATION FAILED: Privacy policy not accepted', privacyPolicyAccepted);
      setError('❌ You must accept the Privacy Policy to proceed. Please check the Privacy Policy checkbox.');
      return false;
    }

    // All validations passed successfully
    console.log('✅ VALIDATION PASSED - All fields valid');
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // 🆕 TRIPLE VERIFICATION: Log IMMEDIATELY to confirm handler executes
    console.log('🚀🚀🚀 HANDLER DEFINITELY FIRED - TRIPLE CHECK');
    console.log('📋 Current form data:', formData);
    
    // 🆕 CRITICAL DEBUG: Log submission attempt
    console.log('🚀 FORM SUBMISSION STARTED');
    console.log('📋 Current form data:', formData);
    
    // Validate form before submission
    console.log('🔍 Starting form validation...');
    const isValid = validateForm();
    console.log('🔍 Validation result:', isValid);
    
    if (!isValid) {
      console.error('❌ FORM SUBMISSION BLOCKED: Validation failed');
      return;
    }
    
    console.log('✅ Validation passed - proceeding with API call');
    
    // 🔧 FIX: Use only React state - no direct DOM manipulation
    setIsSubmitting(true);
    setError('');
    
    try {
      // Convert formatted phone to E.164 format for Supabase storage
      const e164Phone = convertToE164Format(formData?.mobile);
      
      console.log('📱 Converted phone to E.164:', e164Phone);
      console.log('🔄 Calling SMSQuestionnaireService.submitQuestionnaire...');
      
      // 🚀 CRITICAL FIX: Use ONLY Supabase service (no Vercel API)
      const supabaseResult = await SMSQuestionnaireService?.submitQuestionnaire({
        phoneNumber: e164Phone,
        detaineeName: formData?.detaineeName,
        relationshipToDetainee: formData?.relationshipToDetainee,
        detaineeLocation: formData?.location || 'Unknown Location',
        urgencyLevel: formData?.urgencyLevel,
        financialCapability: formData?.financialCapability,
        smsConsent: formData?.smsConsent,
        termsAccepted: formData?.termsAccepted,
        privacyPolicyAccepted: formData?.privacyPolicyAccepted,
        leadData: { name: formData?.name },
        skipSMS: false
      });
      
      console.log('✅ API call completed:', supabaseResult);
      
      if (!supabaseResult?.success) {
        console.error('⚠️ Supabase submission failed:', supabaseResult);
        throw new Error(supabaseResult?.error || 'Failed to submit emergency request');
      }
      
      console.log('✅ Emergency request submitted successfully with detainee name');
      
      // 🆕 CRITICAL FIX: Check if SMS actually sent or failed
      // supabaseResult contains smsResult from sendInitialContactSMS and sendEmergencyAgentNotifications
      const smsSucceeded = supabaseResult?.smsResult?.success === true;
      const agentNotificationFailed = supabaseResult?.smsResult?.agentNotification?.success === false;
      const customerConfirmationFailed = supabaseResult?.smsResult?.customerConfirmation?.success === false;
      
      const smsError = supabaseResult?.smsResult?.error;
      const requiresCredentialFix = supabaseResult?.smsResult?.requiresCredentialFix === true;
      const requiresAgentPhoneConfig = supabaseResult?.smsResult?.agentNotification?.requiresAgentPhoneConfig === true;
      const requiresDeployment = supabaseResult?.smsResult?.agentNotification?.requiresDeployment === true;
      const smsUserMessage = supabaseResult?.smsResult?.userMessage || supabaseResult?.smsResult?.agentNotification?.userMessage;
      const technicalAction = supabaseResult?.smsResult?.agentNotification?.technicalAction;
      
      if (smsSucceeded && !agentNotificationFailed) {
        // ✅ SUCCESS - Request submitted AND both customer + agent SMS sent successfully
        setSmsResults({
          systemAvailable: true,
          agentNotification: { success: true },
          customerConfirmation: { success: true },
          fallbackMode: false
        });
      } else if (agentNotificationFailed) {
        // ⚠️ PARTIAL SUCCESS - Customer SMS sent but AGENT notification failed
        console.warn('⚠️ Emergency request saved but AGENT SMS notification failed:', smsError);
        console.warn('📋 Technical action required:', technicalAction);
        
        // 🆕 ENHANCED: Provide specific guidance based on failure mode
        let adminGuidance = '';
        if (requiresDeployment) {
          adminGuidance = '🔧 ADMIN ACTION: Deploy send-emergency-sms Edge Function in Supabase Dashboard';
        } else if (requiresAgentPhoneConfig) {
          adminGuidance = '🔧 ADMIN ACTION: Add EMERGENCY_AGENT_PHONE_1 and EMERGENCY_AGENT_PHONE_2 to Supabase secrets';
        } else if (requiresCredentialFix) {
          adminGuidance = '🔧 ADMIN ACTION: Update Twilio credentials in Supabase Edge Function secrets';
        } else {
          adminGuidance = '🔧 ADMIN ACTION: Check Supabase Edge Function logs for detailed error information';
        }
        
        setSmsResults({
          systemAvailable: false,
          agentNotification: { 
            success: false,
            requiresDeployment,
            requiresAgentPhoneConfig,
            requiresCredentialFix
          },
          customerConfirmation: { success: !customerConfirmationFailed },
          fallbackMode: true,
          smsFailureNotification: {
            userMessage: smsUserMessage || '📱 Agent notifications temporarily unavailable. Your emergency request has been saved and our team will contact you directly.',
            requiresAdminAction: requiresCredentialFix || requiresAgentPhoneConfig || requiresDeployment,
            technicalDetails: technicalAction || 'Agent SMS delivery failed - check Supabase configuration',
            adminGuidance
          }
        });
      } else {
        // ⚠️ PARTIAL SUCCESS - Request saved but SMS completely failed
        console.warn('⚠️ Emergency request saved but SMS notification system failed:', smsError);
        setSmsResults({
          systemAvailable: false,
          agentNotification: { success: false },
          customerConfirmation: { success: false },
          fallbackMode: true,
          smsFailureNotification: {
            userMessage: smsUserMessage || '📱 SMS notifications are temporarily unavailable. Your emergency request has been saved and our team will contact you directly.',
            requiresAdminAction: requiresCredentialFix,
            technicalDetails: requiresCredentialFix 
              ? '🔧 Twilio credentials need to be updated in Supabase Edge Function secrets.' :'SMS service temporarily unavailable.'
          }
        });
      }
      
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        name: '',
        mobile: '',
        detaineeName: '',
        location: '',
        relationshipToDetainee: '',
        urgencyLevel: 'high',
        financialCapability: '',
        smsConsent: false,
        termsAccepted: false,
        privacyPolicyAccepted: false
      });
      
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setError(`❌ ERROR\n\nWe couldn't submit your request online.\n\nPlease call us immediately at:\n(916) 226-3934\n\nWe're available 24/7 for emergencies.`);
    } finally {
      // 🔧 FIX: Use only React state
      setIsSubmitting(false);
    }
  };

  const getLegalDocument = (type) => {
    return legalDocuments?.find(doc => doc?.document_type === type);
  };

  const renderLegalModal = () => {
    if (!showLegalModal) return null;

    const document = getLegalDocument(showLegalModal);
    if (!document) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">{document?.title}</h3>
              <button
                onClick={() => setShowLegalModal(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
              {document?.content}
            </pre>
          </div>
          <div className="p-6 border-t">
            <button
              onClick={() => setShowLegalModal(null)}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-8 text-center shadow-2xl">
        <div className="text-7xl mb-6">🚨✅</div>
        <h3 className="text-3xl font-black text-green-800 mb-4">Emergency Request Received!</h3>
        <div className="space-y-4 mb-8">
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-center text-green-800 mb-3">
              <CheckCircle className="w-6 h-6 mr-3" />
              <span className="font-bold text-lg">Your Emergency Request Was Saved Successfully</span>
            </div>
            <div className="text-green-700 text-base space-y-2">
              <p>✅ Emergency dashboard is actively monitoring your request</p>
              <p>📞 Licensed agent will call you within 2 minutes at: <strong className="text-lg">{formData?.mobile}</strong></p>
              <p>🚨 Your request is processed as HIGHEST PRIORITY</p>
            </div>
          </div>

          {/* 🆕 IMPROVED: Agent notification status with detailed guidance */}
          {smsResults?.fallbackMode && smsResults?.agentNotification?.success === false && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center text-yellow-800 mb-3">
                <AlertCircle className="w-6 h-6 mr-3" />
                <span className="font-bold text-lg">⚠️ Agent SMS Notification Issue</span>
              </div>
              <div className="text-yellow-700 text-sm space-y-2">
                <p className="font-semibold">✅ Your emergency request was saved successfully and is being monitored.</p>
                <p>📱 Agent SMS notifications are temporarily unavailable, but this does NOT affect your emergency response.</p>
                <p className="font-semibold text-yellow-800">📞 An agent will call you directly within 2 minutes.</p>
                
                {smsResults?.smsFailureNotification?.adminGuidance && (
                  <div className="mt-3 p-3 bg-yellow-100 rounded-lg border border-yellow-400">
                    <p className="text-yellow-900 text-xs font-semibold">
                      {smsResults?.smsFailureNotification?.adminGuidance}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 🆕 IMPROVED: Less prominent SMS notification that emphasizes success */}
          {smsResults?.fallbackMode && !smsResults?.agentNotification && smsResults?.smsFailureNotification?.userMessage && (
            <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 mb-6">
              <div className="text-blue-800 mb-2">
                <span className="font-semibold text-sm">ℹ️ SMS Notification Status</span>
              </div>
              <div className="text-blue-700 text-xs space-y-1">
                <p className="font-semibold">✅ Your emergency request is being processed - agent will call you directly.</p>
                <p className="text-blue-600">📱 SMS notifications are temporarily unavailable, but this does NOT affect your emergency response.</p>
                {smsResults?.smsFailureNotification?.requiresAdminAction && (
                  <p className="text-blue-600 mt-2">
                    ⚙️ Our technical team has been notified to restore SMS functionality.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ✅ SUCCESS: SMS Sent Successfully */}
          {!smsResults?.fallbackMode && smsResults?.agentNotification?.success && (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center text-blue-800 mb-3">
                <CheckCircle className="w-6 h-6 mr-3" />
                <span className="font-bold text-lg">📱 SMS Notifications Sent Successfully</span>
              </div>
              <p className="text-blue-700 text-sm">
                ✅ Emergency agents have been notified via SMS<br/>
                {formData?.smsConsent && smsResults?.customerConfirmation?.success && (
                  <>✅ Confirmation SMS sent to your phone<br/></>
                )}
                📞 Expect a call within 2 minutes
              </p>
            </div>
          )}
        </div>
        {/* SMS Consent Confirmation */}
        {formData?.smsConsent && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-center text-blue-800 mb-3">
              <Shield className="w-6 h-6 mr-3" />
              <span className="font-bold text-lg">SMS Communication Activated</span>
            </div>
            <p className="text-blue-700 text-base">
              ✅ You've opted in to receive SMS updates about your emergency request.<br/>
              📱 Reply STOP to any message to opt out at any time.
            </p>
          </div>
        )}
        {/* Emergency Priority Status */}
        <div className="bg-red-100 border-2 border-red-300 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center text-red-800 mb-3">
            <Clock className="w-6 h-6 mr-3" />
            <span className="font-black text-xl">EMERGENCY PRIORITY RESPONSE</span>
          </div>
          <p className="text-red-700 text-lg font-bold">
            📞 Licensed agent will contact you within 2 minutes
          </p>
          <p className="text-red-600 text-base mt-2">
            Emergency bail assistance is our highest priority
          </p>
        </div>
        <div className="space-y-4">
          <a 
            href={`tel:${fallbackPhone?.replace(/\D/g, '')}`}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-2xl font-black text-xl flex items-center justify-center w-full transition-all transform hover:scale-105 shadow-2xl"
          >
            <Phone className="w-6 h-6 mr-3" />
            Can't Wait? Call EMERGENCY Line: {fallbackPhone}
          </a>
          
          <button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '',
                mobile: '',
                detaineeName: '',
                location: '',
                relationshipToDetainee: '',
                urgencyLevel: 'high',
                financialCapability: '',
                smsConsent: false,
                termsAccepted: false,
                privacyPolicyAccepted: false
              });
            }}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Submit Another Emergency Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-red-200">
      {/* Emergency Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center bg-gradient-to-r from-red-100 to-red-200 text-red-800 px-6 py-3 rounded-full mb-6 border-2 border-red-300">
          <span className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></span>
          <span className="font-black text-lg">🚨 EMERGENCY BAIL BOND ASSISTANCE</span>
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-4">
          Get Immediate Help Now
        </h2>
        <p className="text-lg text-gray-700 font-semibold">
          Licensed agent will contact you within 2 minutes
        </p>
      </div>

      {/* Emergency Features */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="flex items-center p-4 bg-green-50 rounded-xl border border-green-200">
          <Clock className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
          <div>
            <div className="font-bold text-green-800">2-Minute Response</div>
            <div className="text-sm text-green-600">Guaranteed contact</div>
          </div>
        </div>
        
        <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-200">
          <Shield className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" />
          <div>
            <div className="font-bold text-blue-800">24/7 Available</div>
            <div className="text-sm text-blue-600">Never closed</div>
          </div>
        </div>
        
        <div className="flex items-center p-4 bg-purple-50 rounded-xl border border-purple-200">
          <Users className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0" />
          <div>
            <div className="font-bold text-purple-800">Licensed Agents</div>
            <div className="text-sm text-purple-600">15+ years experience</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-8">
          <div className="flex items-center text-red-800 mb-3">
            <AlertCircle className="w-6 h-6 mr-3" />
            <span className="font-bold text-lg">Emergency Submission Error</span>
          </div>
          <p className="text-red-700 text-base mb-3">{error}</p>
          <div className="bg-red-100 p-4 rounded-lg">
            <p className="text-red-800 font-bold text-base">
              🚨 IMMEDIATE EMERGENCY HELP:
            </p>
            <a 
              href={`tel:${fallbackPhone?.replace(/\D/g, '')}`}
              className="text-red-600 font-black text-lg hover:underline"
            >
              Call {fallbackPhone} RIGHT NOW
            </a>
          </div>
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        noValidate 
        className="space-y-6"
      >
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
              Your Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData?.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
              placeholder="Your full name"
            />
          </div>

          <div>
            <label htmlFor="mobile" className="block text-sm font-bold text-gray-700 mb-2">
              Your Phone Number *
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData?.mobile}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
              placeholder="(916) 555-0123"
            />
          </div>
        </div>

        <div>
          <label htmlFor="detaineeName" className="block text-sm font-bold text-gray-700 mb-2">
            Person Who Needs Bail (Full Name) *
          </label>
          <input
            type="text"
            id="detaineeName"
            name="detaineeName"
            value={formData?.detaineeName}
            onChange={handleChange}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
            placeholder="Full name of person in jail"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <label htmlFor="relationshipToDetainee" className="block text-sm font-bold text-gray-700 mb-2">
              Your Relationship to This Person *
            </label>
            <select
              id="relationshipToDetainee"
              name="relationshipToDetainee"
              value={formData?.relationshipToDetainee}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
            >
              <option value="">Select relationship</option>
              <option value="spouse">Spouse/Partner</option>
              <option value="parent">Parent</option>
              <option value="child">Child</option>
              <option value="sibling">Brother/Sister</option>
              <option value="family">Other Family Member</option>
              <option value="friend">Friend</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="urgencyLevel" className="block text-sm font-bold text-gray-700 mb-2">
              Urgency Level *
            </label>
            <select
              id="urgencyLevel"
              name="urgencyLevel"
              value={formData?.urgencyLevel}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
            >
              <option value="critical">🚨 CRITICAL - Need help NOW</option>
              <option value="high">🔴 HIGH - Very urgent</option>
              <option value="medium">🟡 MEDIUM - Urgent</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-bold text-gray-700 mb-2">
            Jail or County Location
          </label>
          <select
            id="location"
            name="location"
            value={formData?.location}
            onChange={handleChange}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
          >
            <option value="">Select location if known</option>
            <option value="Sacramento County">Sacramento County</option>
            <option value="Placer County">Placer County</option>
            <option value="El Dorado County">El Dorado County</option>
            <option value="Yolo County">Yolo County</option>
            <option value="San Joaquin County">San Joaquin County</option>
            <option value="Yuba County">Yuba County</option>
            <option value="Sonoma County">Sonoma County</option>
            <option value="Stanislaus County">Stanislaus County</option>
            <option value="Unknown Location">Unknown Location</option>
          </select>
          <p className="text-sm text-gray-500 mt-2">Don't worry if you don't know - we'll help you find them</p>
        </div>

        <div>
          <label htmlFor="financialCapability" className="block text-sm font-bold text-gray-700 mb-2">
            Budget Range for Bail Bond Services *
          </label>
          <select
            id="financialCapability"
            name="financialCapability"
            value={formData?.financialCapability}
            onChange={handleChange}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
          >
            <option value="">Select budget range</option>
            <option value="Under $1,000">Under $1,000</option>
            <option value="$1,000 - $5,000">$1,000 - $5,000</option>
            <option value="$5,000 - $15,000">$5,000 - $15,000</option>
            <option value="$15,000 - $50,000">$15,000 - $50,000</option>
            <option value="Over $50,000">Over $50,000</option>
            <option value="Need to discuss payment options">Need to discuss payment options</option>
          </select>
          <p className="text-sm text-gray-500 mt-2">Select your available budget or choose "Need to discuss payment options"</p>
        </div>

        {/* SMS Consent Section */}
        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            SMS Communication Consent
          </h3>
          <div className="space-y-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
                <p className="text-sm text-yellow-800">
                  <strong>Our registered local telephone number:</strong> (916) 299-9182
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <input
                type="checkbox"
                name="smsConsent"
                checked={formData?.smsConsent}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="ml-3 text-sm text-gray-700">
                <strong>Yes, I consent to receive SMS messages</strong> from Sailors Bail Bonds at the registered telephone number (916) 299-9182 regarding emergency bail bond services for my loved one. I understand I can opt out at any time by replying STOP to any message. *
              </label>
            </div>
            <p className="text-xs text-gray-600 ml-7">
              Standard message and data rates may apply. You may receive multiple SMS messages related to your specific emergency case.
            </p>
          </div>
        </div>

        {/* Legal Agreements Section */}
        <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Legal Agreements & TCPA Compliance
          </h3>
          <div className="space-y-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData?.termsAccepted}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="ml-3 text-sm text-gray-700">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowLegalModal('terms_of_service')}
                  className="text-red-600 hover:text-red-800 underline font-semibold"
                >
                  Terms of Service
                </button>
                {' '}for SMS communications and emergency bail bond services *
              </label>
            </div>
            <div className="flex items-start">
              <input
                type="checkbox"
                name="privacyPolicyAccepted"
                checked={formData?.privacyPolicyAccepted}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="ml-3 text-sm text-gray-700">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowLegalModal('privacy_policy')}
                  className="text-red-600 hover:text-red-800 underline font-semibold"
                >
                  Privacy Policy
                </button>
                {' '}for SMS communications and agree to be contacted as per Twilio's TCPA requirements *
              </label>
            </div>
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>TCPA Compliance:</strong> By checking these boxes, you expressly consent to receive autodialed and/or pre-recorded calls and text messages from Sailors Bail Bonds at the telephone number you provided regarding bail bond services. Consent is not required to purchase services. Reply STOP to opt out.
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-5 rounded-2xl font-black text-xl transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center disabled:opacity-50 disabled:transform-none border-2 border-red-500"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-white mr-4"></div>
              Sending Emergency Alert...
            </>
          ) : (
            <>
              <Send className="w-7 h-7 mr-4" />
              🚨 SUBMIT EMERGENCY REQUEST → Get Immediate Help
            </>
          )}
        </button>

        <div className="text-center">
          <p className="text-base text-gray-600 mb-2">
            Can't wait? Call our emergency line immediately:
          </p>
          <a 
            href={`tel:${fallbackPhone?.replace(/\D/g, '')}`}
            className="text-red-600 font-black text-xl hover:underline"
          >
            {fallbackPhone}
          </a>
        </div>
      </form>

      {/* Emergency Trust Indicators */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-black text-green-600">2min</div>
            <div className="text-sm text-gray-600">Response Time</div>
          </div>
          <div>
            <div className="text-2xl font-black text-blue-600">24/7</div>
            <div className="text-sm text-gray-600">Always Available</div>
          </div>
          <div>
            <div className="text-2xl font-black text-purple-600">15+</div>
            <div className="text-sm text-gray-600">Years Experience</div>
          </div>
        </div>
      </div>

      {/* Legal Documents Modal */}
      {renderLegalModal()}
    </div>
  );
};

export default EmergencyBailAssistanceForm;